import { HTTPErrorType } from 'dy-frontend-http-repository/lib/data/enums';
import { HTTPErrorResponse } from 'dy-frontend-http-repository/lib/data/types';
import { useStore } from 'effector-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ConfirmationPopover from '@app/components/ConfirmationPopover';
import RouterLink from '@app/components/RouterLink';
import DevText from '@app/components/Text';
import { openModal } from '@app/containers/modals/store/events';
import { Endpoints } from '@app/data/consts';
import { ToastUtils } from '@app/data/utils';
import { usePageTitle, useScrollToTop } from '@app/hooks';
import { Button, Card, Classes, Colors, Icon, Intent, Spinner } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import NonIdealState from '@components/NonIdealState';

import { $client } from '../../store/states';

import CreateClientTeamConnectionModal, {
    CreateClientTeamConnectionModalProps,
} from './modals/CreateClientTeamConnectionModal';
import { resetTeams } from './modals/CreateClientTeamConnectionModal/store/events';
import { teamConnectionsApi } from './store/apis';
import { fetchTeamConnections, removeTeamConnection } from './store/effects';
import { resetTeamConnections } from './store/events';
import { $teamConnections } from './store/states';

const ClientConnections: React.FC = () => {
    const navigate = useNavigate();
    const { scrollToTop } = useScrollToTop();
    const { changeTitle } = usePageTitle('Client Connections');

    const [removingTeamConnectionId, setRemovingTeamConnectionId] = useState<ID | null>(null);

    const client = useStore($client);
    const teamConnections = useStore($teamConnections);

    useEffect(() => {
        if (!client) {
            return;
        }

        const handleFetchTeamConnections = async () => {
            try {
                await fetchTeamConnections({ id: client.id });
            } catch (e) {
                // Log
                console.error(e);

                const response = e.response as HTTPErrorResponse;
                if (response.data.type === HTTPErrorType.MISSING) {
                    // Show error message
                    ToastUtils.showToast({
                        message: `Brand profiles for client with ID ${client.id} was not found`,
                        intent: Intent.DANGER,
                    });

                    // Go to client tasks page
                    navigate(Endpoints.CLIENT_TASKS.replace(':clientId', client.id));
                }
            }
        };

        // Fetch team connections
        handleFetchTeamConnections();

        // Scroll page to top
        scrollToTop();
    }, []);

    useEffect(() => {
        if (!client) {
            return;
        }

        changeTitle(`Client Connections - ${client.first_name} ${client.last_name}`);
    }, [client?.first_name, client?.last_name]);

    useEffect(() => {
        resetTeamConnections();
        resetTeams();
    }, []);

    const handleRemoveTeamConnection = async (connectionId: ID, teamId: ID) => {
        if (!client) {
            return;
        }

        setRemovingTeamConnectionId(connectionId);

        try {
            // Remove team connection
            await removeTeamConnection({ userId: client.id, teamId });

            // Remove team connection from local storage
            teamConnectionsApi.remove({ connectionId });
        } catch (e) {
            // TODO: handle error
            console.error(e);
        } finally {
            setRemovingTeamConnectionId(null);
        }
    };

    const renderAttachTeamButton = () => {
        if (!teamConnections || teamConnections.items.length !== 0) {
            return null;
        }

        return (
            <Button
                className="ml-1"
                icon="add"
                intent={Intent.PRIMARY}
                onClick={() =>
                    openModal<CreateClientTeamConnectionModalProps>({
                        ModalComponent: CreateClientTeamConnectionModal,
                        data: { userId: client?.id ?? '0' },
                    })
                }
            >
                Attach
            </Button>
        );
    };

    const renderTeamConnections = () => {
        const renderList = () => {
            // Team connections were NOT fetched yet
            if (!teamConnections) {
                return (
                    <Flex justify="center">
                        <Spinner />
                    </Flex>
                );
            }

            // Team connections were fetched, but where are no team connections existed for this client
            if (teamConnections.items.length === 0) {
                return (
                    <NonIdealState
                        icon={<Icon className="mb-2" icon="search" size={70} />}
                        title={
                            <Heading type="h4" className="mb-1">
                                No team connections were found
                            </Heading>
                        }
                    />
                );
            }

            return teamConnections.items.map((connection) => (
                <Card className="mb-2" key={connection.id}>
                    <Flex direction="row" justify="space-between" align="center">
                        <Flex direction="row" align="center">
                            <Icon className="mr-2" icon="team" size={40} color={connection.team.color} />

                            <Flex direction="column">
                                <RouterLink
                                    className="mb-small"
                                    color={Colors.WHITE}
                                    to={Endpoints.TEAM_MEMBERS.replace(':teamId', connection.team.id)}
                                >
                                    <Heading type="h6" style={{ color: 'inherit' }}>
                                        {connection.team.title}
                                    </Heading>
                                </RouterLink>

                                <DevText muted>{connection.team.summary}</DevText>
                            </Flex>
                        </Flex>

                        <ConfirmationPopover
                            title="Are you sure want to remove team connection?"
                            description="When confirmed, client-team connection will be removed"
                            actions={[
                                <Button
                                    intent={Intent.DANGER}
                                    className={Classes.POPOVER_DISMISS}
                                    onClick={() => handleRemoveTeamConnection(connection.id, connection.team.id)}
                                >
                                    Yes, remove
                                </Button>,
                            ]}
                        >
                            <Button
                                minimal
                                loading={
                                    removingTeamConnectionId !== null && removingTeamConnectionId === connection.id
                                }
                                disabled={removingTeamConnectionId !== null}
                                icon="cross"
                                intent={Intent.DANGER}
                            />
                        </ConfirmationPopover>
                    </Flex>
                </Card>
            ));
        };

        return (
            <div>
                <Flex className="mb-2" direction="row" align="center" justify="space-between">
                    <Heading type="h3">Team</Heading>
                    {renderAttachTeamButton()}
                </Flex>

                {renderList()}
            </div>
        );
    };

    return <div>{renderTeamConnections()}</div>;
};

export default ClientConnections;
